import { Button, Dialog, DialogContent, DialogContentText, DialogTitle, FormControl, InputLabel, MenuItem, Pagination, Select, Slide, TextField, Tooltip } from '@mui/material'
import React, { useEffect, useState } from 'react'
import { CalendarAPI } from '../../api/CalendarAPI'
import { useDispatch, useSelector } from 'react-redux';
import { TbCalendarCheck } from "react-icons/tb";
import { RiProgress3Line } from "react-icons/ri";
import { MdOutlineFreeCancellation } from "react-icons/md";
import Loader from '../../component/Loader';
import { PiCurrencyDollarFill } from "react-icons/pi";
import { PaymentAPI } from '../../api/PaymentAPI';
import { calenderAction } from '../../redux/reducer/calenderReducer';
import { TiDeleteOutline } from "react-icons/ti";
import { MdOutlineErrorOutline } from "react-icons/md";
import { Privileges } from '../../util/PrivilegeConstants';
import { BsDashLg } from "react-icons/bs";
import { isClubOwner, isClubStaff } from '../../util/UserRoles';
import { PrivilegeAPI } from '../../api/PrivilegesAPI';
import { rootAction } from '../../redux/reducer/rootReducer';
import { clubShowsReservationPayment } from '../../util/calendarAccess';

const timeOptions = {
    timeZone: 'UTC',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
};

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="down" ref={ref} {...props} />;
});

const formatWhen = (val) => val ? new Date(val).toLocaleString('en-US', timeOptions) : '';

const ReservationStatus = ({ val }) => {
    if (val === 'RESERVED') {
        return (
            <>
                <TbCalendarCheck data-testid="reservation_status_incon_reserved" className='reservation_status_incon_reserved' />
                <span>{val.charAt(0).toUpperCase() + val.slice(1).toLowerCase()}</span>
            </>
        );
    }
    if (val === 'IN-PROGRESS') {
        return (
            <>
                <RiProgress3Line data-testid="reservation_status_incon_inprogress" className="reservation_status_incon_inprogress" />
                <span>{val.replace('-', ' ').charAt(0).toUpperCase() + val.replace('-', ' ').slice(1).toLowerCase()}</span>
            </>
        );
    }
    if (val === 'CANCELED ADMIN' || val === 'CANCELED STAFF') {
        return (
            <Tooltip title={`Canceled by ${val?.substring(9, 10) + val?.substring(10).toLowerCase()}`} placement="top">
                <div style={{ display: 'flex' }}>
                    <MdOutlineFreeCancellation data-testid="reservation_status_incon_canceled" className="reservation_status_incon_canceled" />
                    <span>{val?.charAt(0).toUpperCase() + val.substring(1, 8).toLowerCase()}</span>
                </div>
            </Tooltip>
        );
    }
    if (val === 'DELETED') {
        return (
            <>
                <TiDeleteOutline data-testid="reservation_status_incon_deleted" className="reservation_status_incon_canceled" />
                <span>{val?.charAt(0).toUpperCase() + val.slice(1).toLowerCase()}</span>
            </>
        );
    }
    if (val === 'NOT-PAID') {
        return (
            <>
                <MdOutlineErrorOutline data-testid="reservation_status_incon_notpaid" className="reservation_status_incon_canceled" />
                <span>{val?.replace('-', ' ').charAt(0).toUpperCase() + val.replace('-', ' ').slice(1).toLowerCase()}</span>
            </>
        );
    }
    return val ? <span>{val}</span> : <BsDashLg />;
};

const ReservationHistory = () => {
    const userInfo = useSelector((store) => store.user.userInfo);
    const selectedClub = useSelector((store) => store.user.selectedClub);
    const { pageNumber, pageSize } = useSelector((store) => store.calendar);
    const dispatch = useDispatch();

    const [reservations, setReservations] = useState([]);
    const [loading, setLoading] = useState(false);
    const [openPopup, setOpenPopup] = useState(false);
    const [amount, setAmount] = useState('');
    const [reason, setReason] = useState('REQUESTED_BY_CUSTOMER');
    const [reservationId, setReservationId] = useState('');
    const [refresh, setRefresh] = useState(false);
    const [count, setCount] = useState(0);
    const [privileges, setPrivileges] = useState({});
    const showPayment = clubShowsReservationPayment(selectedClub);
    const showRefundAction = showPayment && (isClubOwner(userInfo) || isClubStaff(userInfo));
    const pageCount = count && pageSize ? Math.ceil(count / pageSize) : 0;

    const fetchAllReservation = async () => {
        try {
            setLoading(true);
            const response1 = await CalendarAPI.getAllGames(selectedClub.clubId);
            const games = Array.isArray(response1) ? response1 : (response1?.Data || []);
            const gameId = games[0]?.gameId || selectedClub?.games?.[0]?.gameId;
            if (!gameId) {
                setReservations([]);
                setCount(0);
                return;
            }
            const response2 = await CalendarAPI.getAllReservationHistory({
                clubId: selectedClub.clubId,
                gameId,
                userId: userInfo?.userId,
                isUserAdmin: isClubOwner(userInfo),
                isUserStaff: isClubStaff(userInfo),
                pageSize: pageSize,
                pageNumber: pageNumber
            });
            const rows = Array.isArray(response2?.Data) ? response2.Data : [];
            setReservations(rows);
            setCount(rows[0]?.totalReservation || 0);
        } catch (error) {
            setReservations([]);
            setCount(0);
        } finally {
            setLoading(false);
        }
    }

    const handleRefund = (price, rId) => {
        setAmount(price);
        setReservationId(rId);
        setOpenPopup(true)
    }
    const handleRefundAPI = async (event) => {
        event.preventDefault();
        setOpenPopup(false);
        try {
            const refundInfo = {
                userId: userInfo.userId,
                clubId: selectedClub.clubId,
                reservationId: reservationId,
                reason: reason,
                amount: amount,
            }
            setLoading(true);
            const response = await PaymentAPI.postCreateRefund(refundInfo);

            dispatch(rootAction.setWarningMessage({id:Date.now(), type: 'success', content: response.Data }));
            setRefresh(true);
        } catch (error) {
            dispatch(rootAction.setWarningMessage({ id: Date.now(), type: 'error', content: 'Please contact customer support' }));
        } finally {
            setLoading(false);
        }
    }

    const findAllPrivileges = async () => {
        try {
            setLoading(true)
            const reqObj = {
                userId: userInfo.userId,
                featureName: Object.keys(Privileges)[6]
            }
            const response = await PrivilegeAPI.getAllPrivilegesByFeature(reqObj);
            setPrivileges(response?.Data || {});

        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (userInfo.userId) {
            findAllPrivileges();
        }
    }, [userInfo.userId])

    useEffect(() => {
        if (selectedClub?.clubId) {
            fetchAllReservation();
        }
        setRefresh(false);
    }, [selectedClub?.clubId, refresh, pageNumber, pageSize]);

    return (
        <div className='reservation_history_container'>
            {loading && <Loader />}
            <div className="setup-guide calendar-settings-intro reservation-history-intro">
                <p className="setup-guide-kicker">Calendar</p>
                <h1 className="setup-guide-title">History</h1>
            </div>
            <div className="member-list-table">
                <div className="reservation_history_table_wrap">
                    <table className="reservation_history_table">
                        <thead>
                            <tr>
                                <th>Order Id</th>
                                <th>Start Time</th>
                                <th>End Time</th>
                                <th>Court</th>
                                <th>Status</th>
                                <th>Created By</th>
                                {showPayment && <th>Amount</th>}
                                {showRefundAction && <th>Action</th>}
                            </tr>
                        </thead>
                        <tbody>
                            {reservations.length === 0 && (
                                <tr>
                                    <td colSpan={6 + (showPayment ? 1 : 0) + (showRefundAction ? 1 : 0)} className="reservation_history_empty">
                                        No reservations yet.
                                    </td>
                                </tr>
                            )}
                            {reservations.map((row, index) => {
                                const paidAmount = Number(row.amount);
                                return (
                                    <tr key={row.reservationId || row.reservationOrderId || index}>
                                        <td>{row.reservationOrderId || <BsDashLg />}</td>
                                        <td>{formatWhen(row.startTime)}</td>
                                        <td>{formatWhen(row.endTime)}</td>
                                        <td>{row.resourceName}</td>
                                        <td>
                                            <div className="reservation_history_status">
                                                <ReservationStatus val={row.reservationStatus} />
                                            </div>
                                        </td>
                                        <td>{row.reservationCreatedByName}</td>
                                        {showPayment &&
                                        <td>
                                            <div className="reservation_history_amount">
                                                <PiCurrencyDollarFill className="reservation_amount_incon_reserved" />
                                                {Number.isFinite(paidAmount) ? paidAmount.toFixed(2) : '0.00'}
                                            </div>
                                        </td>}
                                        {showRefundAction && (
                                            <td>
                                                {(paidAmount !== 0 && row.reservationStatus === 'RESERVED' && privileges?.RESERVATION?.includes(Privileges.DELETE)) &&
                                                    <button
                                                        onClick={() => handleRefund(paidAmount.toFixed(2), row.reservationId)}
                                                        data-testid="reservation_refund_button"
                                                        className='reservation_refund_button'
                                                        type='button'>
                                                        Refund
                                                    </button>
                                                }
                                                {(paidAmount !== 0 && (row.reservationStatus === 'CANCELED ADMIN' || row.reservationStatus === 'CANCELED STAFF')) &&
                                                    <button
                                                        disabled
                                                        data-testid="reservation_refund_button_disabled"
                                                        className='reservation_refund_button disabled'
                                                        type='button'>
                                                        Refunded
                                                    </button>
                                                }
                                            </td>
                                        )}
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
                {pageCount > 0 &&
                    <div className='reservation_history_pagination'>
                        <Pagination
                            count={pageCount}
                            page={Math.min(pageNumber || 1, pageCount)}
                            onChange={(event, value) => {
                                dispatch(calenderAction.setPageNumber(value));
                            }}
                            className="reservation-custom-pagination"
                            variant="outlined" />
                    </div>}
            </div>
            <Dialog
                open={openPopup}
                TransitionComponent={Transition}
                keepMounted
                onClose={() => setOpenPopup(false)}
            >
                <DialogTitle sx={{ fontFamily: 'sans-serif', fontWeight: 600 }}>{"Send refund"}</DialogTitle>
                <DialogContent>
                    <DialogContentText component={'span'} sx={{ marginTop: 1 }} id="alert-dialog-slide-description">
                        <form className="reservation-refund-form" onSubmit={handleRefundAPI}>
                            <p>Refunds take 5-10 days to appear on a customer's statement. Fees for the original payment won't be returned, but there are no additional fees for the refund. </p>
                            <div className="reservation-refund-input">
                                <TextField
                                    fullWidth
                                    className="reservation-amount-text-input"
                                    variant="outlined"
                                    name="firstName"
                                    value={amount || ''}
                                    label="Amount *" />
                                <FormControl
                                    fullWidth
                                    data-testid="game-dropdown" className='calender-game-dropdown'
                                >
                                    <InputLabel id="demo-simple-select-autowidth-label">Reason</InputLabel>
                                    <Select
                                        labelId="demo-simple-select-autowidth-label"
                                        id="demo-simple-select-autowidth"
                                        value={reason || ''}
                                        onChange={(e) => setReason(e.target.value)}
                                        label="Reason"
                                    >
                                        <MenuItem value="REQUESTED_BY_CUSTOMER">Request from customer</MenuItem>
                                        <MenuItem value="FRAUDULENT">Fraudulent</MenuItem>
                                        <MenuItem value="DUPLICATE">Duplicate</MenuItem>
                                    </Select>
                                </FormControl>
                            </div>
                            <div className="member-details-btn">
                                <Button
                                    onClick={() => setOpenPopup(false)}
                                    color="error"
                                    variant="outlined">Cancel</Button>

                                <Button
                                    type="submit"
                                    variant="contained">Refund</Button>
                            </div>
                        </form>
                    </DialogContentText>
                </DialogContent>
            </Dialog>
        </div>
    )
}

export default ReservationHistory
